import * as THREE from 'three';
import React from 'react';
import { dedup } from '@gltf-transform/functions';
import { Mesh, Scene, Color } from 'three';
import { GLTF, GLTFNode, GLTFMaterial, IShareScenario } from "common";
import { PropertyType, WebIO } from '@gltf-transform/core';
import MaterialAsset from './MaterialAsset';
import { GetTimelines } from './timeline';
import {Timeline, TimelineExportData} from './timeline/Timeline';
import APIUtility, { SaveTypes } from './APIUtility';
import Utils from './Utilities';
import Scenario from './scenarioElements/Scenario';
import ARTracker from "./ARTracker";
import ProjectSaver from './ProjectSaver';
import * as BufferGeometryUtils from './three-extensions/jsm/utils/BufferGeometryUtils';
import ScopeLight from './Light';
import ScopeCamera from './Camera';
import ErrorHandler, { ErrorCategory, ErrorSeverity, assert } from './utilities/ErrorHandling';
import QuizManager from './QuizManager';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import ColorUtils from './utilities/ColorUtils';
import AssetsManager from './AssetsManager';
import Engine from './Engine';
import { GLTFExporter } from './GLTFExporter';
import { TimelineFragmentExportData } from './timeline/TimelineFragment';
import { JsonCopy, replaceURLParam } from './utilities/Misc';
import { AppState } from './AppState';
import GQLscenario from './api/GQL/scenario/GQLscenario';
import GQLprojects from './api/GQL/projects/GQLprojects';
import MessagePrompter from '../components/prompts/MessagePrompter';
import Spacer from '../components/layout/Spacer';
import { ErrorIcon, Spinner, DownloadIcon } from '../components/Iconset';
import Panel from '../components/layout/Panel';
import NotificationPrompter from '../components/prompts/NotificationPrompter';
import {
	CustomEventTypes,
	MaterialTypes,
	TrackerTypes,
	SaveStates,
	LayoutOrientations,
	PublishTypes,
	RenderModes,
	AllowRetriesOptions,
	TimelineEffectorTypes
} from '../globals';
import store, { StoreActions } from '../state/store';
import {ObjectTriggerTypes, ProjectLinkModes, VisibilityModes, BillboardModes} from "../interfaces/enums";
import { getAssetsFromGrapesJS, getStepConnectionsFromGrapesJS, isConnectedToPreviousStep } from '../components/visageUI/GrapesJSUtilities';
import { IObjectSelection } from '../interfaces/Misc';
import { UIData } from "../interfaces/UITemplateData";
import { ScenarioExportData } from '../interfaces/ScenarioElements';
import { Trigger } from '../interfaces/Trigger';
import StandbyOverlay from '../components/layout/StandbyOverlay';
import config from '../config';

export type ExportGLTF = Omit<GLTF, 'materials'> & {
	materials?: (GLTFMaterial | undefined)[]; //undefined if the material is about to be deleted
};
  
type billboardExtras = 
{
	billboard: {
		target: number;

	}
}

function projectSaveFailed(error)
{
	ErrorHandler.reportError(error, ErrorCategory.ProjectSave, ErrorSeverity.Error);
	MessagePrompter.showError(
		{
			code: 20004,
			title: "Error saving the project",
			content: <div>
				Could not save project.<br />
				If the problem persists, please <a className="highlighted" href="https://scopear.zendesk.com/hc/en-us/requests/new" target="_blank" rel="noopener noreferrer help">contact support</a>.
				<br />
				Error Details: {error.message ? error.message : error}
			</div>,
			actions: [
				{
					label: "Ok",
					onClick: () =>
					{
						StandbyOverlay.stopStandbyMode();
					},
				}
			],
		}
	);
}

export default class ProjectPacker
{
	static instance: ProjectPacker;

	referencedAssets: Set<string> = new Set<string>();
	prePackageSelection : IObjectSelection = {objects: [], sceneGuid: ""};
	prePackageTurnedOffObjects: THREE.Object3D[] = [];

	objectGuidToNodeIndexMap: Map<string, number> = new Map();
	sceneIdToIndexMap: Map<string, number> = new Map();
	nodeIndexToCameraIndex: Map<number, number> = new Map();
	deletedMaterialIndices: number[] = [];
	hiddenMaterialMeshes: number[] = [];
	exportedTriggerNodesToValidate: GLTFNode[] = [];
	exportedBillboardTargets: billboardExtras[] = [];
	modelTrackersAssetIndexMap = new Map();
	materialAssetIndexMap = new Map();
	openInInteractiveMode = false;
	forceAllowCameraMode = false;

	static getInstance()
	{
		if (!ProjectPacker.instance) ProjectPacker.instance = new ProjectPacker();
		return ProjectPacker.instance;
	}

	deleteProperty = (jsonObj: object, property: string) =>
	{
		// TODO: why do we only delete the property if it has a non-undefined value? there's no comment explaining this :(
		if (jsonObj && jsonObj[property] !== undefined)
		{
			delete jsonObj[property];
		}
	}

	resetData()
	{
		this.referencedAssets.clear();
		this.deletedMaterialIndices.length = 0;
		this.exportedTriggerNodesToValidate.length = 0;
		this.exportedBillboardTargets.length = 0;
		this.hiddenMaterialMeshes.length = 0;

		this.objectGuidToNodeIndexMap.clear();
		this.nodeIndexToCameraIndex.clear();
		this.sceneIdToIndexMap.clear();
		this.modelTrackersAssetIndexMap.clear();
		this.materialAssetIndexMap.clear();
	}

	modifyScenesData = (jsonObj: ExportGLTF) =>
	{
		if(!jsonObj.scenes) return;

		let modelTrackersSceneIndex: number | undefined = undefined;
		for (let i = 0; i < jsonObj.scenes.length; i++)
		{
			const scene = jsonObj.scenes[i];
			if (scene.extras.isModelTrackersScene)
			{
				modelTrackersSceneIndex = i;
			}
			this.sceneIdToIndexMap.set(scene.extras.guid, i);
			this.deleteProperty(scene, "extras");
		}

		if (modelTrackersSceneIndex !== undefined)
		{
			const scene = jsonObj.scenes[modelTrackersSceneIndex];
			for (const nodeIndex of scene.nodes)
			{
				const node = jsonObj.nodes![nodeIndex];
				if (node.extras.trackerIndices)
				{
					for (const trackerindex of node.extras.trackerIndices)
					{
						this.modelTrackersAssetIndexMap.set(trackerindex, nodeIndex);
					}
					this.deleteProperty(node.extras, "trackerIndices");
				}
			}
			assert(modelTrackersSceneIndex === jsonObj.scenes.length - 1);
			jsonObj.scenes.splice(modelTrackersSceneIndex, 1);
		}
	}

	modifyMaterialsData = (jsonObj: ExportGLTF) =>
	{
		if (!jsonObj.materials) return;

		const modifyMaterial = (material: GLTFMaterial, materialIndex :number ) =>
		{
			const materialAsset = MaterialAsset.getMaterialAssetByGuid(material.extras.guid);
			if (!materialAsset)
			{
				//no material asset assigned. this means were dealing with an internal material that is not represnted by a materialAsset
				assert(material.extras.type && "missing type. did you forget to add material.userData.type in customMaterials?");
				const materialType = material.extras.type;
				switch (materialType)
				{
					case "modelRoot":
						this.deletedMaterialIndices.push(materialIndex);
						break;
					case 'textMat':
					//should be treated as diffuse unlit material
						material.extras.type = MaterialTypes.DiffuseUnlit;
						break;
					default:
						assert(false && "unhandled type");
						break;
				}
			}
			else
			{
				const materialType = materialAsset.type;
				material.extras.type = materialType;
				//insert any material-specific properties here
				switch (materialType)
				{
					case MaterialTypes.Video:
						material.extras.asset = MaterialAsset.getPacakgedVideoMaterialAsset(materialAsset.guid);
						break;
					case MaterialTypes.Line:
						material.extras.lineThickness = materialAsset.lineThickness;
						material.extras.lineColor = materialAsset.lineColor.toArray();
						break;
					default:
						break;
				}
			}

			this.deleteProperty(material, "extensions");
		}

		for (let i = 0; i < jsonObj.materials.length; i++)
		{
			const material = jsonObj.materials[i]!;
			modifyMaterial(material, i);
			const materialAsset = MaterialAsset.getMaterialAssetByGuid(material.extras.guid);
			if (materialAsset && materialAsset.type === MaterialTypes.Video)
			{
				const videoAssetId = MaterialAsset.getPacakgedVideoMaterialAsset(material.extras.guid);
				if (videoAssetId && !this.referencedAssets.has(videoAssetId))
				{
					this.referencedAssets.add(videoAssetId);
				}
			}
			let indexDelta = 0;
			for (const index of this.deletedMaterialIndices)
			{
				if (i > index)
				{
					indexDelta++;
				}
			}

			this.materialAssetIndexMap.set(material.extras.guid, i - indexDelta);
			this.deleteProperty(material.extras, "guid");
		}

	}

	modifyMeshesData = (jsonObj: ExportGLTF) =>
	{
		if (!jsonObj.meshes) return;

		for (let i = 0; i < jsonObj.meshes.length; i++)
		{
			const mesh = jsonObj.meshes[i];
			if (mesh.primitives)
			{
				assert(mesh.primitives.length === 1); //the gltfexporter always creates a combined mesh, never multi-primitives in one mesh (unless it detects multi-materials, which we aren't expected to ever have)
				const primitive = mesh.primitives[0];
				if (primitive.material !== undefined)
				{
					const materialIndex = primitive.material;
					if (this.deletedMaterialIndices.indexOf(materialIndex) !== -1) //the material refered to by the mesh is about to be deleted
					{
						this.hiddenMaterialMeshes.push(i);
						delete primitive.material;
					}
					else //the material is still valid but its index might have changed
					{
						let indexDelta = 0;
						for (const index of this.deletedMaterialIndices)
						{
							if (materialIndex > index)
							{
								indexDelta++;
							}
						}

						primitive.material = materialIndex - indexDelta;
					}
				}
			}
		}

	}

	modifyNodeAnnotations = (node: GLTFNode) =>
	{
		if (!node?.extras?.annotation) return;

		if (!node.extras.annotation.enabled)
		{
			this.deleteProperty(node.extras, "annotation");
		}
		else
		{
			this.deleteProperty(node.extras.annotation, "enabled");

			if(node.extras.annotation.autoWidth)
			{
				this.deleteProperty(node.extras.annotation, "width");
			}
			else
			{
				node.extras.annotation.width *= 256; //exporting in pixel units
			}

			//convert color properties from srgb to linear space
			node.extras.annotation.titleColor.r = ColorUtils.SRGBToLinear((node.extras.annotation.titleColor.r / 255)) * 255;
			node.extras.annotation.titleColor.g = ColorUtils.SRGBToLinear((node.extras.annotation.titleColor.g / 255)) * 255;
			node.extras.annotation.titleColor.b = ColorUtils.SRGBToLinear((node.extras.annotation.titleColor.b / 255)) * 255;

			node.extras.annotation.textColor.r = ColorUtils.SRGBToLinear((node.extras.annotation.textColor.r / 255)) * 255;
			node.extras.annotation.textColor.g = ColorUtils.SRGBToLinear((node.extras.annotation.textColor.g / 255)) * 255;
			node.extras.annotation.textColor.b = ColorUtils.SRGBToLinear((node.extras.annotation.textColor.b / 255)) * 255;

			node.extras.annotation.titleBackground.r = ColorUtils.SRGBToLinear((node.extras.annotation.titleBackground.r / 255)) * 255;
			node.extras.annotation.titleBackground.g = ColorUtils.SRGBToLinear((node.extras.annotation.titleBackground.g / 255)) * 255;
			node.extras.annotation.titleBackground.b = ColorUtils.SRGBToLinear((node.extras.annotation.titleBackground.b / 255)) * 255;

			node.extras.annotation.backgroundColor.r = ColorUtils.SRGBToLinear((node.extras.annotation.backgroundColor.r / 255)) * 255;
			node.extras.annotation.backgroundColor.g = ColorUtils.SRGBToLinear((node.extras.annotation.backgroundColor.g / 255)) * 255;
			node.extras.annotation.backgroundColor.b = ColorUtils.SRGBToLinear((node.extras.annotation.backgroundColor.b / 255)) * 255;

			node.extras.annotation.lineColor.r = ColorUtils.SRGBToLinear((node.extras.annotation.lineColor.r / 255)) * 255;
			node.extras.annotation.lineColor.g = ColorUtils.SRGBToLinear((node.extras.annotation.lineColor.g / 255)) * 255;
			node.extras.annotation.lineColor.b = ColorUtils.SRGBToLinear((node.extras.annotation.lineColor.b / 255)) * 255;
		}
	}

	modifyNodeBillboards = (node: GLTFNode) =>
	{
		if (!node?.extras?.billboard) return;

		if (!node.extras.billboard.enabled)
		{
			this.deleteProperty(node.extras, "billboard");
		}
		else
		{
			if (node.extras.billboard.mode === BillboardModes.LookAt)
			{
				if (node.extras.billboard.targetGuid)
				{
					node.extras.billboard.target = node.extras.billboard.targetGuid;
					this.exportedBillboardTargets.push(node.extras as billboardExtras);
				}
				else
				{
					this.deleteProperty(node.extras, "billboard");
				}
			}
			else
			{
				node.extras.billboard.target = null;
			}

			this.deleteProperty(node.extras.billboard, "mode");
			this.deleteProperty(node.extras.billboard, "targetGuid");
			this.deleteProperty(node.extras.billboard, "enabled");
		}

	}

	modifyNodeTriggers = (node: GLTFNode) =>
	{
		if (!node?.extras?.triggers) return;

		node.extras.triggers = node.extras.triggers.filter((trigger) => !trigger.removedSyncedTrigger);
		if (!node.extras.triggers.length || node.extras.discardTriggerExport)
		{
			this.deleteProperty(node.extras, "discardTriggerExport");
			this.deleteProperty(node.extras, "triggers");
		}
		else
		{
			let requiresValidation = false;
			for (let i = 0; i < node.extras.triggers.length; i++)
			{
				const triggerType = node.extras.triggers[i].type;
				switch (triggerType)
				{
					case ObjectTriggerTypes.MoveToStep:
					case ObjectTriggerTypes.VisibilityToggle:
					case ObjectTriggerTypes.CalloutToggle:
						requiresValidation = true;
						break;
					case ObjectTriggerTypes.ProjectLink:
						switch (node.extras.triggers[i].projectLinkMode)
						{
							case ProjectLinkModes.Online:
								node.extras.triggers[i].scenarioName = node.extras.triggers[i].onlineScenarioName;
								break;
							case ProjectLinkModes.Offline:
								node.extras.triggers[i].scenarioName = node.extras.triggers[i].offlineScenarioName;
								this.deleteProperty(node.extras.triggers[i], "scenarioId");
								break;
						}
						break;
				}

				if(triggerType !== ObjectTriggerTypes.MoveToStep)
				{
					this.deleteProperty(node.extras.triggers[i], "targetStep");
				}
				if(triggerType !== ObjectTriggerTypes.ProjectLink)
				{
					this.deleteProperty(node.extras.triggers[i], "projectLinkMode");
					this.deleteProperty(node.extras.triggers[i], "scenarioId");
				}
				if(triggerType !== ObjectTriggerTypes.URL)
				{
					this.deleteProperty(node.extras.triggers[i], "url");
				}
				if(triggerType !== ObjectTriggerTypes.VisibilityToggle)
				{
					this.deleteProperty(node.extras.triggers[i], "targetObjectVisibility");
					if(triggerType !== ObjectTriggerTypes.CalloutToggle)
					{
						this.deleteProperty(node.extras.triggers[i], "targetObject");
					}
				}
				else if(node.extras.triggers[i].targetObjectVisibility !== VisibilityModes.Custom)
				{
					this.deleteProperty(node.extras.triggers[i], "customMaterial");
				}

				this.deleteProperty(node.extras.triggers[i], "enabled");
				this.deleteProperty(node.extras.triggers[i], "offlineScenarioName");
				this.deleteProperty(node.extras.triggers[i], "onlineScenarioName");
				this.deleteProperty(node.extras.triggers[i], "defaultSyncedTrigger");
			}
			if (requiresValidation) this.exportedTriggerNodesToValidate.push(node);
		}
	}

	modifyNodeVisibility = (node: GLTFNode) =>
	{
		if (!node?.extras?.visibility) return;

		if(node.extras.visibility[RenderModes.Device] === VisibilityModes.Custom)
		{
			const materialIndex = this.materialAssetIndexMap.get(node.extras.visibility.customDeviceMaterial);
			if(materialIndex !== undefined)
			{
				node.extras.visibility.customDeviceMaterial = materialIndex;
			}
			else
			{
				node.extras.visibility[RenderModes.Device] = VisibilityModes.Solid;
				this.deleteProperty(node.extras.visibility, "customDeviceMaterial");
			}
		}
		else
		{
			this.deleteProperty(node.extras.visibility, "customDeviceMaterial");
		}

		if(node.extras.visibility[RenderModes.Standalone] === VisibilityModes.Custom)
		{
			const materialIndex = this.materialAssetIndexMap.get(node.extras.visibility.customStandaloneMaterial);
			if(materialIndex !== undefined)
			{
				node.extras.visibility.customStandaloneMaterial = materialIndex;
			}
			else
			{
				node.extras.visibility[RenderModes.Standalone] = VisibilityModes.Solid;
				this.deleteProperty(node.extras.visibility, "customStandaloneMaterial");
			}
		}
		else
		{
			this.deleteProperty(node.extras.visibility, "customStandaloneMaterial");
		}
	}

	modifyNodeInteractiveCamera = (jsonObj: ExportGLTF, nodeIndex: number) =>
	{
		const node = jsonObj.nodes![nodeIndex];
		if (!node?.extras?.isInteractiveModeCamera) return;
		const cameraIndex = this.nodeIndexToCameraIndex.get(nodeIndex)! //this will always exist, the map was just populated
		const camera = jsonObj.cameras!.at(cameraIndex)!; //this is never going to be undefined
		if (!camera.extras) camera.extras = {};
		camera.extras.isInteractiveModeCamera = true;
		this.deleteProperty(node.extras, "isInteractiveModeCamera");
	}

	modifyNodeMetadata = (node: GLTFNode) =>
	{
		if (!node?.extras?.metaData) return;

		for(const key of Object.keys(node.extras.metaData))
		{
			node.extras.metaData[key] = node.extras.metaData[key].value;
		}
	}

	addTimelineExtras = (jsonObj: ExportGLTF) =>
	{
		jsonObj.extras.timelineData = Timeline.exportTimelines();
		for (const value of Object.values<TimelineExportData>(jsonObj.extras.timelineData.timelines))
		{
			const fragments = value.fragments as TimelineFragmentExportData[];
			for (const fragment of fragments)
			{
				assert(fragment.persistentSceneObjectIds!.length);
				fragment.nodes = [];
				for (const guid of fragment.persistentSceneObjectIds!)
				{
					assert(this.objectGuidToNodeIndexMap.has(guid));
					fragment.nodes.push(this.objectGuidToNodeIndexMap.get(guid)!);
				}
				delete fragment.persistentSceneObjectIds;

				switch (fragment.effectorType)
				{
					case TimelineEffectorTypes.ObjectColor:
						{
							const linearColor = new Color().setHex(parseInt('0x' + fragment['targetValue']));
							fragment['targetValue'] = linearColor.getHexString();
						}
						break;
					case TimelineEffectorTypes.HighlightSphere:
						{
							const linearColor = new Color().setHex(parseInt('0x' + fragment['color']));
							fragment['color'] = linearColor.getHexString();
						}
						break;
					case TimelineEffectorTypes.EventInvocation:
						{
							if (fragment['target'] === ObjectTriggerTypes.MoveToStep)
							{
								const targetStep = Scenario.getStep(fragment['params']);
								if (!targetStep || targetStep.isDisabledTrackersStep())
								{
									fragment['params'] = null;
								}
							}
							if(fragment['target'] === ObjectTriggerTypes.VisibilityToggle && fragment['params'] === VisibilityModes.Custom)
							{
								const materialIndex = this.materialAssetIndexMap.get(fragment['customMaterial']);
								if(materialIndex !== undefined)
								{
									fragment['customMaterial'] = materialIndex;
								}
								else
								{
									fragment['params'] = VisibilityModes.Solid;
									delete fragment['customMaterial'];
								}
							}
							else
							{
								delete fragment['customMaterial'];
							}
						}
						break;
					default:
						break;

				}
			}
		}
	}

	addTrackerDataExtras = (jsonObj: ExportGLTF) =>
	{
		jsonObj.extras.trackersData = ARTracker.save(this.referencedAssets, true);

		for (let i = 0; i < jsonObj.extras.trackersData.length; i++)
		{
			const tracker = jsonObj.extras.trackersData[i];
			if (tracker.type === TrackerTypes.MODEL && tracker.model)
			{
				if (tracker.initPoseCamera !== undefined)
				{
					const initPoseCameraNode = this.objectGuidToNodeIndexMap.get(tracker.initPoseCamera)!;
					tracker.initPoseCamera = jsonObj.nodes![initPoseCameraNode].camera;
				}
				tracker.ghostGuideNodeIndex = this.modelTrackersAssetIndexMap.get(i);
			}
		}
	}

	addSequenceDataExtras = (jsonObj: ExportGLTF) =>
	{
		const scenarioData = Scenario.export(this.objectGuidToNodeIndexMap) as ScenarioExportData;

		for (const value of Object.values(scenarioData.steps))
		{
			const sceneGuid = value.sceneId!;
			value.sceneId = this.sceneIdToIndexMap.get(sceneGuid) as any;
		}

		jsonObj.extras.sequenceData = scenarioData;
	}

	addUIDataExtras = (jsonObj: ExportGLTF) =>
	{
		const uiData = ProjectSaver.getInstance().saveUIData(this.referencedAssets, true) as UIData;
		for (const stepData of Object.values(uiData))
		{
			if (stepData)
			{
				for (const key of Object.keys(stepData))
				{
					if (key !== 'active')
					{
						const templateData = stepData[key];
						const subcomponents = Object.values(templateData);
						for (const subcomponentData of subcomponents)
						{
							const properties = Object.values(subcomponentData);
							for (let i = 0; i < properties.length; i++)
							{
								const propertyData = properties[i];
								if (propertyData.type === 'camera' && propertyData.value)
								{
									if (this.objectGuidToNodeIndexMap.has(propertyData.value as string))
									{
										const nodeIndex = this.objectGuidToNodeIndexMap.get(propertyData.value as string);
										const cameraIndex = this.nodeIndexToCameraIndex.get(nodeIndex!);
										propertyData.value = cameraIndex;
									}
									else
									{
										propertyData.value = "";
									}
								}
							}
						}
					}
				}
			}
		}

		jsonObj.extras.uiData = uiData;
	}

	addNewUIDataExtras = async (jsonObj: ExportGLTF) =>
	{
		// new ui data - webviews
		jsonObj.extras.webViews = {};
		const stepUIData = JsonCopy(store.getState().visageModule.stepUIData);
		for (const stepId of Object.keys(stepUIData))
		{
			const step  = Scenario.getStep(stepId);
			// skip steps that are missing, deleted, or don't have a ui template
			if (!step || step.isDeleted() || !step.uiTemplateId)
			{
				continue;
			}

			const usedAssets = getAssetsFromGrapesJS(stepUIData[stepId]);
			usedAssets.forEach(assetId => this.referencedAssets.add(assetId));
			jsonObj.extras.webViews[stepId] = [
				{
					id: stepId,
					cutouts: [],
					assets: await Promise.all(usedAssets.map(async (assetId) =>
					{
						const assetExtension = await Utils.fetchAssetFileExtension(assetId);
						return assetExtension ? `${assetId}.${assetExtension.toLowerCase()}` : assetId;
					})),
					linkedSteps: getStepConnectionsFromGrapesJS(stepUIData[stepId], stepId),
					linkedPreviousStep: isConnectedToPreviousStep(stepUIData[stepId], stepId),
				},
			];
		}
	}

	addQuizesDataExtras = (jsonObj: ExportGLTF) =>
	{
		if(QuizManager.getQuizType())
		{
			jsonObj.extras.quizSettings = JSON.parse(JSON.stringify(store.getState().checklistModule.quizSettings));
			if(jsonObj.extras.quizSettings.allowRetries && jsonObj.extras.quizSettings.allowRetries !== AllowRetriesOptions.NEVER)
			{
				jsonObj.extras.quizSettings.gradedAttempts++;
			}
			else
			{
				jsonObj.extras.quizSettings.gradedAttempts = 1;
			}
		}
		else
		{
			jsonObj.extras.quizSettings = {};
		}
	}

	addPartsCatalogDataExtras = (jsonObj: ExportGLTF) =>
	{
		jsonObj.extras.partsCatalog = ProjectSaver.getInstance().savePartsCatalog();
	}

	addStepConditionsDataExtras = (jsonObj: ExportGLTF) =>
	{
		jsonObj.extras.stepConditions = {};
		const stepConditions = store.getState().scenarioModule.stepConditions;
		for(const [key, value] of Object.entries(stepConditions))
		{
			if (jsonObj.extras.sequenceData.steps[key])
			{
				const valueObject = JSON.parse(JSON.stringify(value)); // deep copy to avoid frozen objects
				for(let j = 0; j < valueObject.length; j++)
				{
					const condition = valueObject[j];
					for (let i = 0; i < condition.triggers?.length; i++)
					{
						const triggerCopy = JSON.parse(JSON.stringify(condition.triggers[i])) as Trigger;
						if(triggerCopy.targetObject)
						{
							const objectGuid = triggerCopy.targetObject;
							triggerCopy.targetObject = this.objectGuidToNodeIndexMap.get(objectGuid) as any;
						}

						const cleanTrigger = {type: triggerCopy.type} as any;
						switch(triggerCopy.type)
						{
							case ObjectTriggerTypes.MoveToStep:
								cleanTrigger.targetStep = triggerCopy.targetStep;
								break;
							case ObjectTriggerTypes.URL:
								cleanTrigger.url = triggerCopy.url;
								break;
							case ObjectTriggerTypes.ProjectLink:
								cleanTrigger.projectLinkMode = triggerCopy.projectLinkMode;
								if(triggerCopy.projectLinkMode === ProjectLinkModes.Online)
								{
									cleanTrigger.scenarioName = triggerCopy.onlineScenarioName;
									cleanTrigger.scenarioId = triggerCopy.scenarioId;
								}
								else
								{
									cleanTrigger.scenarioName = triggerCopy.offlineScenarioName;
								}
								break;
							case ObjectTriggerTypes.VisibilityToggle:
								cleanTrigger.targetObject = triggerCopy.targetObject;
								cleanTrigger.targetObjectVisibility = triggerCopy.targetObjectVisibility;
								break;
							case ObjectTriggerTypes.CalloutToggle:
								cleanTrigger.targetObject = triggerCopy.targetObject;
								break;
						}

						valueObject[j].triggers[i] = cleanTrigger;
					}
				}

				jsonObj.extras.stepConditions[key] = valueObject;
			}
		}
	}

	addAttachmentsDataExtras = (jsonObj: ExportGLTF) =>
	{
		const attachmentsData = JSON.parse(JSON.stringify(store.getState().attachmentsModule.attachments)); // deep copy to avoid frozen objects
		for (const stepId of Object.keys(attachmentsData))
		{
			// cleaning up deleted steps data
			if (Scenario.getStep(stepId)?.isDeleted())
			{
				delete attachmentsData[stepId];
				continue;
			}

			// cleaning steps with no assigned attachments
			attachmentsData[stepId] = attachmentsData[stepId].filter(attachmentId => AssetsManager.getPropsForAsset(attachmentId));
			if(!attachmentsData[stepId].length)
			{
				delete attachmentsData[stepId];
				continue;
			}

			for(let i = 0; i < attachmentsData[stepId].length; i++)
			{
				const attachmentId = attachmentsData[stepId][i];
				const assetProps = AssetsManager.getPropsForAsset(attachmentId);

				// add reference to attachment asset
				if (!this.referencedAssets.has(attachmentId))
				{
					this.referencedAssets.add(attachmentId);
				}

				// include file extension in exported data for the app
				attachmentsData[stepId][i] = {
					id: attachmentId,
					extension: Utils.getFileExtension(assetProps!.label),
					fileName: Utils.getFileNameNoExtension(assetProps!.label),
				};
			}
		}
		jsonObj.extras.attachmentsData = attachmentsData;
	}

	addChecklistDataExtras = (jsonObj: ExportGLTF) =>
	{
		jsonObj.extras.checklistData = QuizManager.packQuizReduxData(this.objectGuidToNodeIndexMap);
	}

	addIOTDataExtras = (jsonObj: ExportGLTF) =>
	{
		const iotData = JSON.parse(JSON.stringify(store.getState().iotModule)); // deep copy
		delete iotData["tags"];
		delete iotData["connected"];
		iotData.subscriptionKeys = Object.keys(iotData.subscriptionKeys);
		jsonObj.extras.iotData = iotData;
	}

	disposeNodeExtrasData = (node: GLTFNode) =>
	{
		this.deleteProperty(node.extras, "guid");
		this.deleteProperty(node.extras, "nodeIndex");
		this.deleteProperty(node.extras, "modelData");
		this.deleteProperty(node.extras, "lightType");
		this.deleteProperty(node.extras, "light");
		this.deleteProperty(node.extras, "camera");
		this.deleteProperty(node.extras, "mediaPanel");
		this.deleteProperty(node.extras, "helper");
		this.deleteProperty(node.extras, "animations");
		this.deleteProperty(node.extras, "isModelRoot");
		this.deleteProperty(node.extras, "isSubMesh");
		this.deleteProperty(node.extras, "originalUuid");
		this.deleteProperty(node.extras, "assetId");
		this.deleteProperty(node.extras, "hideInHierarchy");
		this.deleteProperty(node.extras, "name");
		this.deleteProperty(node.extras, "lightColor");
		this.deleteProperty(node.extras, "lightIntensity");
		this.deleteProperty(node.extras, "unlinkedDefaultObjectGuid");
		this.deleteProperty(node.extras, "premoveDefaultObjectGuid");
		this.deleteProperty(node.extras, "defaultObjectGuid");
		this.deleteProperty(node.extras, "primitiveType");
		this.deleteProperty(node.extras, "primitiveScale");
		this.deleteProperty(node.extras, "fadeInFactor");
		this.deleteProperty(node.extras, "overrideColorFactor");
		this.deleteProperty(node.extras, "overrideColor");
		this.deleteProperty(node.extras, "preInteractiveBackupValues");
		this.deleteProperty(node.extras, "markedSaveRoot");
		this.deleteProperty(node.extras, "saveModifications");
		this.deleteProperty(node.extras, "removedSubNodes");
		this.deleteProperty(node.extras, "originalParentGuid");
		this.deleteProperty(node.extras, "groups");
		this.deleteProperty(node.extras, "renderIsDirty");
		this.deleteProperty(node.extras, "anchorDistance");
		this.deleteProperty(node.extras, "requiresOutline");
		this.deleteProperty(node.extras, "isCachedAssetObject");
		this.deleteProperty(node.extras, "pivot");
		this.deleteProperty(node.extras, "activeModifications");
		this.deleteProperty(node.extras, "interactiveCameraFocusPoint");
	}

	finalizeExportedTriggers = () =>
	{
		for(const triggersNode of this.exportedTriggerNodesToValidate)
		{
			for(let i = triggersNode.extras.triggers.length-1; i >= 0; i--)
			{
				switch(triggersNode.extras.triggers[i].type)
				{
					case ObjectTriggerTypes.MoveToStep:
						const targetStep = Scenario.getStep(triggersNode.extras.triggers[i].targetStep);
						if(!targetStep || targetStep.isDisabledTrackersStep())
						{
							triggersNode.extras.triggers.splice(i, 1);
						}
						break;
					case ObjectTriggerTypes.VisibilityToggle:
						{
							const targetIndex = this.objectGuidToNodeIndexMap.get(triggersNode.extras.triggers[i].targetObject);
							if(targetIndex !== undefined)
							{
								triggersNode.extras.triggers[i].targetObject = targetIndex;
								if(triggersNode.extras.triggers[i].targetObjectVisibility === VisibilityModes.Custom)
								{
									const materialIndex = this.materialAssetIndexMap.get(triggersNode.extras.triggers[i].customMaterial);
									if(materialIndex !== undefined)
									{
										triggersNode.extras.triggers[i].customMaterial = materialIndex;
									}
									else
									{
										triggersNode.extras.triggers[i].targetObjectVisibility = VisibilityModes.Solid;
										delete triggersNode.extras.triggers[i].customMaterial;
									}
								}
							}
							else
							{
								triggersNode.extras.triggers.splice(i, 1);
							}
						}
						break;
					case ObjectTriggerTypes.CalloutToggle:
						{
							const targetIndex = this.objectGuidToNodeIndexMap.get(triggersNode.extras.triggers[i].targetObject);
							if(targetIndex !== undefined)
							{
								triggersNode.extras.triggers[i].targetObject = targetIndex;
							}
							else
							{
								triggersNode.extras.triggers.splice(i, 1);
							}
						}
						break;
				}
			}
			if(!triggersNode.extras.triggers.length)
			{
				this.deleteProperty(triggersNode.extras, "triggers");
			}
		}
	}

	finalizeExportedBillboards = () =>
	{
		for (const extras of this.exportedBillboardTargets)
		{
			const targetIndex = this.objectGuidToNodeIndexMap.get(extras.billboard.target as any);
			if (targetIndex !== undefined)
			{
				extras.billboard.target = targetIndex;
			}
			else
			{
				this.deleteProperty(extras, "billboard");
			}
		}
	}

	modifyExportJson = async (jsonObj: ExportGLTF) =>
	{
		this.modifyScenesData(jsonObj);
		this.modifyMaterialsData(jsonObj);
		this.modifyMeshesData(jsonObj);

		if (jsonObj.nodes)
		{
			for (let i = 0; i < jsonObj.nodes.length; i++)
			{
				const node = jsonObj.nodes[i];
				if (node.extras)
				{
					this.objectGuidToNodeIndexMap.set(node.extras.guid, i);

					if (node.extras.audio && node.extras.audio.clip)
					{
						if (!this.referencedAssets.has(node.extras.audio.clip))
						{
							this.referencedAssets.add(node.extras.audio.clip);
						}
					}

					if(node.extras.camera && node.children)
					{
						for (const childIndex of node.children)
						{
							if (jsonObj.nodes[childIndex].camera !== undefined)
							{
								this.nodeIndexToCameraIndex.set(i, jsonObj.nodes[childIndex].camera!);
								break;
							}
						}
					}

					this.modifyNodeAnnotations(node);
					this.modifyNodeBillboards(node);
					this.modifyNodeTriggers(node);
					this.modifyNodeVisibility(node);
					this.modifyNodeInteractiveCamera(jsonObj, i);
					this.modifyNodeMetadata(node);
					this.disposeNodeExtrasData(node);
				}

				if (node.mesh !== undefined)
				{
					const meshIndex = node.mesh;
					if (this.hiddenMaterialMeshes.indexOf(meshIndex) !== -1) //the mesh refered to by the node shouldn't be rendered by the app. removing node.mesh reference
					{
						this.deleteProperty(node, "mesh");
						node.extras["physics-proxy"] = meshIndex;
					}
				}
			}
		}

		if(this.deletedMaterialIndices.length)
		{
			for (const index of this.deletedMaterialIndices)
			{
				jsonObj.materials![index] = undefined;
			}
			jsonObj.materials = jsonObj.materials!.filter(function(el) {return el != undefined;});
		}

		this.finalizeExportedTriggers();
		this.finalizeExportedBillboards();

		if (jsonObj.animations && jsonObj.animations.length === 0) this.deleteProperty(jsonObj, "animations");
		if (!jsonObj.extras) jsonObj.extras = {};

		this.addTrackerDataExtras(jsonObj);
		this.addSequenceDataExtras(jsonObj);
		this.addUIDataExtras(jsonObj);
		await this.addNewUIDataExtras(jsonObj);
		this.addQuizesDataExtras(jsonObj);
		this.addPartsCatalogDataExtras(jsonObj);
		this.addStepConditionsDataExtras(jsonObj);
		this.addAttachmentsDataExtras(jsonObj);
		this.addChecklistDataExtras(jsonObj);
		this.addIOTDataExtras(jsonObj);
		this.addTimelineExtras(jsonObj);

		jsonObj.extras.openInInteractiveMode = this.openInInteractiveMode;
		jsonObj.extras.forceAllowCameraMode = this.forceAllowCameraMode;
		jsonObj.extras.skyboxTexture = Engine.getInstance().envMapAssetId;
	}

	preProjectPackage = () =>
	{
		this.resetData();

		this.prePackageSelection = Engine.getInstance().getPickingManager().getCurrentSelection();
		Engine.getInstance().getPickingManager().clearSelection();

		//we only wanna export whatever is under selection root, so make everything else not visible
		const sceneList = Scenario.getAllScenes();
		for (const scene of sceneList)
		{
			const selectionRoot = Engine.getInstance().getSelectionRoot(scene.userData.guid);
			for (const child of scene.children)
			{
				if (child !== selectionRoot && child.visible)
				{
					child.visible = false;
					this.prePackageTurnedOffObjects.push(child);
				}
			}
		}

		const timelines = GetTimelines();
		for (const timeline of timelines)
		{
			timeline.resetAnimations();
			for (const fragment of timeline.fragments)
			{
				if(fragment.effectorType ===TimelineEffectorTypes.EventInvocation)
				{
					if(fragment.effector.customMaterial)
					{
						const materialAsset = MaterialAsset.getMaterialAssetByGuid(fragment.effector.customMaterial);
						if(materialAsset) materialAsset.forcePackage = true;
					}
				}
			}
		}

		MaterialAsset.preProjectPackage();
		QuizManager.preProjectPackage();
	}

	postProjectPackage = () =>
	{
		for (const object of this.prePackageTurnedOffObjects) object.visible = true;
		this.prePackageTurnedOffObjects.length = 0;

		Scenario.currentStep()?.timeline?.update();

		MaterialAsset.postProjectPackage();
		QuizManager.postProjectPackage();
		Engine.getInstance().getPickingManager().restoreSelection(this.prePackageSelection);
	}

	genereateModelTrackersScene = (trackerModelBlobMap) =>
	{
		//populate a scene with all model instacnes used as model trackers
		const modelTrackersScene = new Scene();
		modelTrackersScene.userData.isModelTrackersScene = true;
		const exportedModelTrackerAssets: string[] = [];

		let exportedTrackerIndex = 0;
		for (const tracker of ARTracker.getTrackerList() as ARTracker[])
		{
			if (tracker.trackerType !== TrackerTypes.MODEL || !tracker.modelAssetId)
			{
				exportedTrackerIndex++;
				continue;
			}

			if (!tracker.autoInit)
			{
				const cameraClone = SceneObjectUtils.cloneObject(tracker.modelTrackerCamera);
				cameraClone.userData.guid = tracker.modelTrackerCamera.userData.guid;
				modelTrackersScene.attach(cameraClone);
				tracker.getCameraExportTransform(cameraClone.position, cameraClone.quaternion);
			}

			if (!exportedModelTrackerAssets.includes(tracker.modelAssetId))
			{
				let exportInstance;
				const modelGeometries: THREE.BufferGeometry[] = [];

				const parent = tracker.modelInstance!.parent!;
				parent.remove(tracker.modelInstance!);
				tracker.modelInstance!.updateMatrixWorld(true);
				tracker.modelInstance!.traverse((child: THREE.Object3D) =>
				{
					if (child instanceof Mesh && child.geometry && child.material.visible && (!(child instanceof ScopeCamera)) && (!(child instanceof ScopeLight)))
					{
						const transformedGeometry = child.geometry.clone() as THREE.BufferGeometry;
						transformedGeometry.applyMatrix4(child.matrixWorld);
						modelGeometries.push(transformedGeometry);
					}
				});
				parent.add(tracker.modelInstance!);

				const mergedGeometry = BufferGeometryUtils.mergeGeometries(modelGeometries);
				for (const geometry of modelGeometries) geometry.dispose();

				if (mergedGeometry)
				{
					exportInstance = new Mesh(BufferGeometryUtils.mergeVertices(mergedGeometry), MaterialAsset.defaultMaterial.internalMaterial!);
					exportInstance.geometry.userData = {};
					exportInstance.userData.assetId = tracker.modelAssetId;
					mergedGeometry.dispose();
				}
				else
				{
					exportInstance = SceneObjectUtils.cloneObject(tracker.modelInstance!);
					exportInstance.traverse((child) =>
					{
						if(child.material) child.material = MaterialAsset.defaultMaterial.internalMaterial;
						if(child.userData.visibility) delete child.userData.visibility;
					});
				}

				exportInstance.userData.trackerIndices = [];
				exportInstance.userData.trackerIndices.push(exportedTrackerIndex);

				exportedModelTrackerAssets.push(tracker.modelAssetId);
				modelTrackersScene.attach(exportInstance);
				const modelInfo = AssetsManager.getAssetInfo(tracker.modelAssetId);
				const extension = modelInfo ? modelInfo.extension ?? Utils.getFileExtension(modelInfo.name) : 'glb';
				if (extension !== 'obj')
				{
					trackerModelBlobMap[tracker.modelAssetId + ".glb"] = tracker.getExportBlob();
				}
			}
			else
			{
				for (const child of modelTrackersScene.children)
				{
					if (child.userData.assetId === tracker.modelAssetId)
					{
						child.userData.trackerIndices.push(exportedTrackerIndex);
					}
				}

			}
			exportedTrackerIndex++;
		}

		return modelTrackersScene;
	}

	exportProject = async (publishing = false, recipientGroups: string[] = [], publishType = PublishTypes.persistent_for_any_user, encrpyted = false, openInInteractiveMode = false, bypassCPS = false, resourcePermissions?: IShareScenario["replaceResourcePermissions"]) =>
	{
		if (store.getState().applicationModule.isPublishInProgress) return;

		if (Utils.IsSimulationRunning()) AppState.stopSimulation();

		store.dispatch(StoreActions.application.setPublishInProgress(true));
		const notification = NotificationPrompter.prompt({
			indismissable: true,
			content:
			<Panel orientation={LayoutOrientations.Horizontal}>
				<div>
					{
						publishing ? "Publishing Project..." : "Downloading Scenario..."
					}
				</div>
				<Spacer size={{ x: "12px" }} />
				<Spinner size={14} />
			</Panel>
		});


		StandbyOverlay.startStandbyMode(publishing ? "Publishing Project" : "Preparing to download Scenario");
		await new Promise((resolve) => setTimeout(resolve, 0)); // to allow the standby mode to show up
		this.preProjectPackage();

		const exporter = new GLTFExporter();
		const animationsList = Engine.getInstance().getExportAnimations();
		const options = { 
			binary: true, 
			animations: animationsList, 
			additionalMaterials: new Array<THREE.Material>()
		};

		for(const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			if(materialAsset.forcePackage)	options.additionalMaterials.push(materialAsset.internalMaterial!);
		}

		const scenesToExport = Scenario.getAllScenes().filter((scene) => !scene.userData.isDefaultScene);

		const trackerModelBlobMap = {};
		const modelTrackersScene = this.genereateModelTrackersScene(trackerModelBlobMap);
		if(modelTrackersScene.children.length)
		{
			scenesToExport.push(modelTrackersScene);
		}
		
		this.openInInteractiveMode = openInInteractiveMode;
		this.forceAllowCameraMode = bypassCPS;

		exporter.parse(scenesToExport, 
			this.modifyExportJson, 
			async (result) =>
			{
				try 
				{
					this.postProjectPackage();
					SceneObjectUtils.disposeObject(modelTrackersScene, false);

					let glb;
					if(result)
					{
						const io = new WebIO();
						try
						{
							const document = await io.readBinary(new Uint8Array(result));
							
							// Check if project has custom materials
							const materials = document.getRoot().listMaterials();
							const hasCustomMaterials = materials.some(material => 
								material.getExtras() && Object.keys(material.getExtras()).length > 0
							);
							
							// Only apply dedup if no custom materials are present
							if (!hasCustomMaterials) 
							{
								await document.transform(...[dedup({propertyTypes: 
									[PropertyType.ACCESSOR,
										PropertyType.MESH,
										PropertyType.TEXTURE,
										PropertyType.SKIN,], keepUniqueNames: false})]);
							}
							else 
							{
								console.log('Skipping dedup to preserve custom materials');
							}
							
							glb = await io.writeBinary(document);
						}
						catch (error)
						{
							console.warn(error);
							glb = result;
						}
					} 
					else 
					{
						glb = result;
					}

					if (publishing)
					{
						if(config.USE_LEGACY_PUBLISH_RESTAPI)
						{
							await APIUtility.publishProject(new Blob([glb], { type: 'application/octet-stream' }), encrpyted, recipientGroups, publishType, Array.from(this.referencedAssets), trackerModelBlobMap, resourcePermissions);
						} 
						else 
						{
							await GQLscenario.publishProject(new Blob([glb], { type: 'application/octet-stream' }), encrpyted, recipientGroups, publishType, Array.from(this.referencedAssets), trackerModelBlobMap, resourcePermissions);		
						}
						NotificationPrompter.removeNotification(notification);
						NotificationPrompter.prompt({ content: "Project was published successfully", timeout: 7 });
						store.dispatch(StoreActions.application.setPublishInProgress(false));
					}
					else
					{
						function downloadFile(url: string, filename: string) 
						{
							const a = document.createElement('a');
							a.href = url;
							a.download = filename; // This will set the download filename
							a.click();
							URL.revokeObjectURL(result.url);
						}
						let data;
						if(config.USE_LEGACY_PUBLISH_RESTAPI)
						{
							data = await APIUtility.publishOffline(new Blob([result], { type: 'application/octet-stream' }),  Array.from(this.referencedAssets), publishType, trackerModelBlobMap)
							window.open(data?.url);
						}
						else
						{
							data = await GQLscenario.publishOffline(new Blob([glb], { type: 'application/octet-stream' }),  Array.from(this.referencedAssets), publishType, trackerModelBlobMap)
							downloadFile(data.url, data.filename);
						}
						
					

						NotificationPrompter.removeNotification(notification);
						NotificationPrompter.prompt({
							content: ["Your scenario is ready to download, please wait for the download to finish.",
								<Panel className={"centerContentVertically"} key={'notification'} orientation={LayoutOrientations.Horizontal}>
									If the download doesn't start automatically, click here: <Spacer size={{ x: "6px" }} 
									/> 
									<DownloadIcon 
										onClick={() => 
										{
											if(1)
											{
												window.open(data?.url);
											}
											else
											{
												downloadFile(data.url, data.filename);
											}
										}}
									/>
								</Panel>], timeout: 0
						});						
					}
				} 
				catch (error) 
				{
					NotificationPrompter.removeNotification(notification);
					this.onExportError(error);
					
				}
				finally
				{
					store.dispatch(StoreActions.application.setPublishInProgress(false));
					StandbyOverlay.stopStandbyMode();
					if(store.getState().applicationModule?.isViewOnlyMode)
					{
						AppState.startSimulation(false);
					}
				}
			}, (error) => 
			{
				NotificationPrompter.removeNotification(notification);
				this.onExportError(error);
				store.dispatch(StoreActions.application.setPublishInProgress(false));
				StandbyOverlay.stopStandbyMode();
				if(store.getState().applicationModule?.isViewOnlyMode)
				{
					AppState.startSimulation(false);
				}
			}, options);
	}

	saveProject = async (duplicateProject = false, saveType = SaveTypes.MANUAL, silent = false, projectId = Scenario.current()?.id || "")  => 
	{
		if (store.getState().applicationModule.isPublishInProgress || !projectId) return;

		if (Utils.IsSimulationRunning()) AppState.stopSimulation();

		APIUtility.saveWlcSettings();

		document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectSaveStart));

		let notification;
		if (!silent)
		{
			notification = NotificationPrompter.prompt({
				indismissable: true,
				content:
				<Panel orientation={LayoutOrientations.Horizontal}>
					<div>
						Saving Project...
					</div>
					<Spacer size={{ x: "12px" }} />
					<Spinner size={14} />
				</Panel>
			});
		}

		StandbyOverlay.startStandbyMode("Saving Project");
		const timelines = GetTimelines();
		for (const timeline of timelines) timeline.resetAnimations();

		const { zip, referencedAssets }  = await ProjectSaver.getInstance().save();
		
		Scenario.currentStep()?.timeline?.update();

		//const output = JSON.stringify(projectData);
		const blob  = await zip.generateAsync({type : "blob"})
		APIUtility.saveProject(blob, saveType, referencedAssets, projectId, duplicateProject).then(async res =>
		{
			if (!res || res.status !== 200 || !res.data.status.toLowerCase().startsWith('success'))
			{
				NotificationPrompter.removeNotification(notification);
				projectSaveFailed(res?.data?.error || "No response data given");
				return;
			}

			document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectSaveEnd, { detail: { resolution: SaveStates.Success } }));
			store.dispatch(StoreActions.history.onProjectSaved());

			if (duplicateProject)
			{ 
				// set the correct new scenario id and name in case this is a duplicated project
				Scenario.current()!.id = res.data.id; // we can ! here because we checked the scenario id above
				store.dispatch(StoreActions.scenario.setElementData({ dataId: res.data.id, data: {name:res.data.name} }));
				replaceURLParam('projectid', res.data.id);

				// set as active scenario
				store.dispatch(StoreActions.scenario.setScenario(res.data.id));
			}

			// TODO: Move project saving over to GQL entirely so we don't have to concern whether or not the api will get the save file id
			if (res.data.saveFileId || process.env.JEST_WORKER_ID)
			{
				store.dispatch(StoreActions.scenario.setProjectSaveFileId(res.data.saveFileId));
			}
			else 
			{
				console.log("No save file id found, getting it from the server");
				const saveFileId = await GQLprojects.getProjectSaveFileId(res.data.id);
				store.dispatch(StoreActions.scenario.setProjectSaveFileId(saveFileId));
			}

			if (!silent)
			{
				NotificationPrompter.removeNotification(notification);
				NotificationPrompter.prompt({ content: "Project saved successfully.", timeout: (saveType === SaveTypes.MANUAL? 7 : 10) });
			}
			StandbyOverlay.stopStandbyMode();
			if(store.getState().applicationModule?.isViewOnlyMode) 
			{
				AppState.startSimulation(false);
			}
		},
		error =>
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectSaveEnd, { detail: { resolution: SaveStates.Error, content: error.message } }));

			NotificationPrompter.removeNotification(notification);
			projectSaveFailed(error);
			if(store.getState().applicationModule?.isViewOnlyMode) 
			{
				AppState.startSimulation(false);
			}
		});
	}

	onExportError = (error) =>
	{
		console.log(error.message);
		NotificationPrompter.prompt({
			content:
				<Panel>
					<Panel className={"largeText"}
						orientation={LayoutOrientations.Horizontal}
					>
						<ErrorIcon />
						Project export has failed!
					</Panel>
					<div>
						{error.message}
					</div>
				</Panel>
		});
	}
}



